import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Text, Heading, Input, SortableTableHeader, LoadingSpinner, Button, Select } from '../components/ui'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { supabase } from '../lib/supabase'
import { 
  CheckCircleIcon, 
  XMarkIcon,
  ServerIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend, LabelList } from 'recharts'

// Helpers de rótulos/tooltip com percentual para Recharts
const renderBarCountPercentLabel = (props: any) => {
	const { value, x, y, width, payload } = props
	if (value == null) return null
	const numericValues = Object.values(payload || {}).filter((v: any) => typeof v === 'number') as number[]
	const total = numericValues.reduce((acc: number, v: number) => acc + v, 0)
	const pct = total > 0 ? Math.round((Number(value) / total) * 100) : 0
	const posX = (x || 0) + (width || 0) / 2
	const posY = (y || 0) - 6
	return (
		<text x={posX} y={posY} fill="#374151" textAnchor="middle" fontSize={12}>
			{`${value} (${pct}%)`}
		</text>
	)
}

const pieLabelText = (entry: any) => {
	const pct = Math.round(((entry?.percent ?? 0) as number) * 100)
	return `${entry?.value ?? 0} (${pct}%)`
}

const pieTooltipFormatter = (value: number, name: string, item: any) => {
	const pctFraction = (item?.percent ?? item?.payload?.percent ?? 0) as number
	const pct = Math.round(pctFraction * 100)
	return [`${value} (${pct}%)`, name]
}

// Tooltip custom para PieCharts: exibe apenas nome e valor (sem %)
const renderPieTooltip = (props: any) => {
	const { active, payload } = props
	if (!active || !payload || payload.length === 0) return null
	return (
		<div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
			{payload.map((p: any) => {
				const value = Number(p?.value) || 0
				return (
					<div key={p?.name || p?.dataKey} className="whitespace-nowrap">
						{`${p?.name}: ${value}`}
					</div>
				)
			})}
		</div>
	)
}

const barTooltipFormatter = (value: number, name: string, item: any) => {
	const payload = item?.payload ?? {}
	const total = Object.values(payload).reduce((acc: number, v: any) => (typeof v === 'number' ? acc + v : acc), 0)
	const pct = total > 0 ? Math.round(((value as number) / total) * 100) : 0
	return [`${value} (${pct}%)`, name]
}

interface AtendimentoData {
	analysis_date: string
	channel: string
	ticket_id: string
	json: any
}

// WhatsApp tables schema (public.wpp_ticket_list / public.wpp_ticket_detail)
interface WppTicketHeader {
  agent: string | null
  protocol: string
  phone: string | null
  start_datetime: string | null
  end_datetime: string | null
  service_duration: number | null
  status: string | null
  rating: number | null
  classification: string | null
}

interface WppTicketMessage {
  id: string
  datetime: string
  protocol: string | null
  message: string | null
  sender: string | null
  type: string | null
}

// Zendesk tables schema (public.zendesk_ticket_list / public.zendesk_ticket_detail / public.zendesk_ticket_users)
interface ZendeskTicketHeader {
  ticket_id: string
  created_at: string
  channel: string | null
  subject: string | null
  to: string | null
  from: string | null
  from_email: string | null
  description: string | null
  status: string | null
  tags: string | null
  url: string | null
  identity: string | null
  assignee_id: number | null
  requester_id: number | null
}

interface ZendeskTicketDetail {
  id: string
  ticket_id: string
  description: string | null
  from: string | null
  to: string | null
  date: string | null
  public: boolean | null
  author_id?: number | null
}

type SortKey =
  | 'attendance_date'
  | 'channel'
  | 'ticket_id'
  | 'protocolo'
  | 'nota'
  | 'urgencia'
  | 'solucionado'
  | 'sentimento'
  | 'sentimentoAtendente'
  | 'compreensao'
  | 'riscoReabertura'
  | 'atendente'
  | 'errosPortugues'
  | 'relacaoErros'
  | 'descricao'
  | 'naoConformidade'
  | 'limitacoesAnalise'
  | 'oportunidades'

const getLast30DaysDates = () => {
  const hoje = new Date()
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  const inicio = new Date(fim)
  inicio.setDate(inicio.getDate() - 30)
  const formatDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return { inicio: formatDate(inicio), fim: formatDate(fim) }
}

const AtendimentoAnalise = () => {
  // Verificar permissão antes de carregar dados
  const { hasPermission, loading: permissionLoading } = usePermission('atendimento_analise', 'show')

  const last30Dates = getLast30DaysDates()

  const [atendimentosData, setAtendimentosData] = useState<AtendimentoData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasInitialLoad, setHasInitialLoad] = useState(false)
  const [filtroCanal, setFiltroCanal] = useState('')
  const [filtroIdAtendimento, setFiltroIdAtendimento] = useState('')
  const [filtroDataInicio, setFiltroDataInicio] = useState(last30Dates.inicio)
  const [filtroDataFim, setFiltroDataFim] = useState(last30Dates.fim)
  const [filtroNota, setFiltroNota] = useState<number[]>([])
  const [filtroUrgencia, setFiltroUrgencia] = useState('')
  const [filtroSolucionado, setFiltroSolucionado] = useState('')
  const [filtroSentimento, setFiltroSentimento] = useState('')
  const [filtroAtendente, setFiltroAtendente] = useState('')
  const [filtroErrosPortugues, setFiltroErrosPortugues] = useState('')
  const [filtroCompreensao, setFiltroCompreensao] = useState('')
  const [filtroRiscoReabertura, setFiltroRiscoReabertura] = useState('')
  const [filtroClassificacao, setFiltroClassificacao] = useState('')
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedAtendimento, setSelectedAtendimento] = useState<AtendimentoData | null>(null)
	 const [activeTab, setActiveTab] = useState<'indicadores' | 'dashboard' | 'indicadoresAtendente'>('indicadores')
  const [detailsTab, setDetailsTab] = useState<'info' | 'dialogo'>('info')
	const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
	// Estado do diálogo (WhatsApp)
	const [dialogLoading, setDialogLoading] = useState(false)
	const [dialogError, setDialogError] = useState<string | null>(null)
	const [dialogHeader, setDialogHeader] = useState<WppTicketHeader | null>(null)
	const [dialogItems, setDialogItems] = useState<WppTicketMessage[]>([])
	// Estado do diálogo (Zendesk)
	const [zdHeader, setZdHeader] = useState<ZendeskTicketHeader | null>(null)
	const [zdItems, setZdItems] = useState<ZendeskTicketDetail[]>([])
	const [zdUsersById, setZdUsersById] = useState<Record<string, { name: string, role: string }>>({})
	const [zdLoading, setZdLoading] = useState(false)
	const [zdError, setZdError] = useState<string | null>(null)
  const [wppHeadersByProtocol, setWppHeadersByProtocol] = useState<Record<string, WppTicketHeader>>({})
  const [zdHeadersByTicketId, setZdHeadersByTicketId] = useState<Record<string, ZendeskTicketHeader>>({})
  
	// Estados de ordenação
	const [sortBy, setSortBy] = useState<SortKey>('attendance_date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Navegação horizontal (aba Análises)
  const analisesScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollAnalisesBy = (dx: number) => {
    analisesScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }
  const handleAnalisesWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      analisesScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  const [filtrosAbertos, setFiltrosAbertos] = useState(false)
  const [notaDropdownOpen, setNotaDropdownOpen] = useState(false)
  const notaDropdownRef = useRef<HTMLDivElement>(null)

  function safeParseJson(data: any): any {
    if (!data) return null as any
    try {
      if (typeof data === 'string') {
        return JSON.parse(data)
      }
      return data
    } catch {
      return null as any
    }
  }

  const fetchAtendimentos = useCallback(async () => {
    if (!hasPermission) return
    if (!filtroDataInicio || !filtroDataFim) {
      setAtendimentosData([])
      return
    }

    try {
      setLoading(true)
      setError(null)

      const startIso = new Date(filtroDataInicio + 'T00:00:00').toISOString()
      const endIso = new Date(filtroDataFim + 'T23:59:59').toISOString()

      const allData: AtendimentoData[] = []
      const batchSize = 1000
      let from = 0
      let hasMore = true

      while (hasMore) {
        const { data, error } = await supabase
          .from('ticket_analysis')
          .select('*')
          .gte('analysis_date', startIso)
          .lte('analysis_date', endIso)
          .order('analysis_date', { ascending: false })
          .range(from, from + batchSize - 1)

        if (error) {
          throw error
        }

        if (data && data.length > 0) {
          allData.push(...data)

          if (data.length < batchSize) {
            hasMore = false
          } else {
            from += batchSize
          }
        } else {
          hasMore = false
        }
      }

      setAtendimentosData(allData)
      setCurrentPage(1)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [hasPermission, filtroDataInicio, filtroDataFim])

  useEffect(() => {
    if (permissionLoading) return
    if (!hasPermission) {
      setLoading(false)
      return
    }
    if (!hasInitialLoad && filtroDataInicio && filtroDataFim) {
      setHasInitialLoad(true)
      fetchAtendimentos()
    }
  }, [permissionLoading, hasPermission, hasInitialLoad, filtroDataInicio, filtroDataFim, fetchAtendimentos])

  // Enriquecer dados com cabeçalhos (datas do atendimento) em lote
  useEffect(() => {
    // Não carregar dados adicionais se não tiver permissão
    if (!hasPermission) {
      return
    }

    const enrichHeaders = async () => {
      try {
        const protocolos = new Set<string>()
        const ticketIds = new Set<string>()

        atendimentosData.forEach((item) => {
          const canal = (item.channel ?? '').toString().toLowerCase()
          const j = safeParseJson(item.json)
          const o = j?.output ?? j
          if (canal.includes('whatsapp')) {
            const p = (o?.protocolo ?? item.ticket_id ?? '') as string
            if (p) protocolos.add(p)
          } else if (canal.includes('zendesk')) {
            const t = (item.ticket_id ?? '') as string
            if (t) ticketIds.add(t)
          }
        })

        if (protocolos.size > 0) {
          const batchSize = 100
          const protocolList = Array.from(protocolos)
          const allWppHeaders: any[] = []
          for (let i = 0; i < protocolList.length; i += batchSize) {
            const chunk = protocolList.slice(i, i + batchSize)
            const { data, error } = await supabase
              .from('wpp_ticket_list')
              .select('*')
              .in('protocol', chunk)
            if (!error && data) allWppHeaders.push(...data)
          }
          const map: Record<string, WppTicketHeader> = {}
          allWppHeaders.forEach((h: any) => {
            if (h && h.protocol) map[h.protocol] = h as WppTicketHeader
          })
          setWppHeadersByProtocol(map)
        } else {
          setWppHeadersByProtocol({})
        }

        if (ticketIds.size > 0) {
          const batchSize = 100
          const ticketIdList = Array.from(ticketIds)
          const allZdHeaders: any[] = []
          for (let i = 0; i < ticketIdList.length; i += batchSize) {
            const chunk = ticketIdList.slice(i, i + batchSize)
            const { data, error } = await supabase
              .from('zendesk_ticket_list')
              .select('*')
              .in('ticket_id', chunk)
            if (!error && data) allZdHeaders.push(...data)
          }
          const map: Record<string, ZendeskTicketHeader> = {}
          allZdHeaders.forEach((h: any) => {
            if (h && h.ticket_id) map[h.ticket_id] = h as ZendeskTicketHeader
          })
          setZdHeadersByTicketId(map)
        } else {
          setZdHeadersByTicketId({})
        }
      } catch {
        setWppHeadersByProtocol({})
        setZdHeadersByTicketId({})
      }
    }
    if (atendimentosData.length > 0) enrichHeaders()
    else {
      setWppHeadersByProtocol({})
      setZdHeadersByTicketId({})
    }
  }, [atendimentosData, hasPermission])

	const canaisDisponiveis = useMemo(() => {
		const canais = atendimentosData.map(item => item.channel).filter(Boolean)
		return Array.from(new Set(canais)).sort()
	}, [atendimentosData])

  const sentimentosDisponiveis = useMemo(() => {
    const valores: string[] = []
    atendimentosData.forEach(item => {
      const j = safeParseJson(item.json)
      const o = j?.output ?? j
      const v = o?.sentimentoCliente
      if (v) valores.push(String(v))
    })
    return Array.from(new Set(valores)).sort()
  }, [atendimentosData])

  const compreensoesDisponiveis = useMemo(() => {
    const valores: string[] = []
    atendimentosData.forEach(item => {
      const j = safeParseJson(item.json)
      const o = j?.output ?? j
      const v = o?.compreensao
      if (v) valores.push(String(v))
    })
    return Array.from(new Set(valores)).sort()
  }, [atendimentosData])

  const atendentesDisponiveis = useMemo(() => {
    const valores: string[] = []
    atendimentosData.forEach(item => {
      const j = safeParseJson(item.json)
      const o = j?.output ?? j
      const v = o?.atendente
      if (v) valores.push(String(v))
    })
    return Array.from(new Set(valores)).sort()
  }, [atendimentosData])

  const errosPortuguesDisponiveis = useMemo(() => {
    const valores: string[] = []
    atendimentosData.forEach(item => {
      const j = safeParseJson(item.json)
      const o = j?.output ?? j
      const v = o?.errosPortuguesAtendente
      if (v) valores.push(String(v))
    })
    return Array.from(new Set(valores)).sort()
  }, [atendimentosData])

  const classificacoesDisponiveis = useMemo(() => {
    const valores: string[] = []
    atendimentosData.forEach(item => {
      const itemCanal = (item.channel ?? '').toString().toLowerCase()
      // Para WhatsApp, buscar do header
      if (itemCanal.includes('whatsapp')) {
        const j = safeParseJson(item.json)
        const o = j?.output ?? j
        const p = (o?.protocolo ?? item.ticket_id ?? '') as string
        const h = p ? wppHeadersByProtocol[p] : undefined
        const v = h?.classification
        if (v) valores.push(String(v))
      }
      // Para outros canais ou se houver no JSON, buscar do JSON
      const j = safeParseJson(item.json)
      const o = j?.output ?? j
      const v = o?.classificacao || o?.tema || o?.classification
      if (v) valores.push(String(v))
    })
    return Array.from(new Set(valores)).sort()
  }, [atendimentosData, wppHeadersByProtocol])

  const atendimentosFiltrados = useMemo(() => {
    const filtroCanalNorm = filtroCanal.toLowerCase()
    const filtroIdAtendimentoNorm = filtroIdAtendimento.toLowerCase()
    const filtroSentimentoNorm = filtroSentimento.toLowerCase()
    const filtroAtendenteNorm = filtroAtendente.toLowerCase()
    const filtroErrosPortNorm = filtroErrosPortugues.toLowerCase()
    const filtroCompreensaoNorm = filtroCompreensao.toLowerCase()
    const filtroClassificacaoNorm = filtroClassificacao.toLowerCase()

		const filtered = atendimentosData.filter(item => {
			const itemCanal = (item.channel ?? '').toString().toLowerCase()
			const itemIdAtendimento = (item.ticket_id ?? '').toString().toLowerCase()
			let attendanceAt: Date | null = null
			try {
				if (itemCanal.includes('whatsapp')) {
					const j0 = safeParseJson(item.json)
					const o0 = j0?.output ?? j0
					const p0 = (o0?.protocolo ?? item.ticket_id ?? '') as string
					const h = p0 ? wppHeadersByProtocol[p0] : undefined
					const dt = (h?.start_datetime || h?.end_datetime) ?? null
					attendanceAt = dt ? new Date(dt as string) : null
				} else if (itemCanal.includes('zendesk')) {
					const h = zdHeadersByTicketId[item.ticket_id]
					const dt = h?.created_at ?? null
					attendanceAt = dt ? new Date(dt as string) : null
				}
			} catch {}
			if (!attendanceAt) {
				attendanceAt = item.analysis_date ? new Date(item.analysis_date) : null
			}
      const j = safeParseJson(item.json)
      const o = j?.output ?? j

      const matchCanal = !filtroCanalNorm || itemCanal.includes(filtroCanalNorm)
      const matchIdAtendimento = !filtroIdAtendimentoNorm || itemIdAtendimento.includes(filtroIdAtendimentoNorm)
      const matchDataInicio = !filtroDataInicio || (attendanceAt && attendanceAt.getTime() >= new Date(filtroDataInicio + 'T00:00:00').getTime())
      const matchDataFim = !filtroDataFim || (attendanceAt && attendanceAt.getTime() <= new Date(filtroDataFim + 'T23:59:59').getTime())
      const matchNota = filtroNota.length === 0 || (typeof o?.nota === 'number' && filtroNota.includes(Number(o.nota)))
      const matchUrgencia = !filtroUrgencia || ((o?.urgencia === true) === (filtroUrgencia === 'sim'))
      const matchSolucionado = !filtroSolucionado || ((o?.solucionado === true) === (filtroSolucionado === 'sim'))
      const matchSentimento = !filtroSentimentoNorm || String(o?.sentimentoCliente || '').toLowerCase() === filtroSentimentoNorm
      const matchAtendente = !filtroAtendenteNorm || String(o?.atendente || '').toLowerCase() === filtroAtendenteNorm
      const errVal = o?.errosPortuguesAtendente
      const matchErrosPort = !filtroErrosPortNorm || String(errVal || '').toLowerCase() === filtroErrosPortNorm
      const comprVal = o?.compreensao
      const matchCompreensao = !filtroCompreensaoNorm || String(comprVal || '').toLowerCase() === filtroCompreensaoNorm
      const matchRiscoReabertura = !filtroRiscoReabertura || ((o?.riscoReabertura === true) === (filtroRiscoReabertura === 'sim'))
      
      // Lógica de filtro de classificação/tema
      let classificacaoVal: string = ''
      if (itemCanal.includes('whatsapp')) {
        const p = (o?.protocolo ?? item.ticket_id ?? '') as string
        const h = p ? wppHeadersByProtocol[p] : undefined
        classificacaoVal = String(h?.classification || '')
      } else {
        classificacaoVal = String(o?.classificacao || o?.tema || o?.classification || '')
      }
      const classificacaoValNorm = classificacaoVal.toLowerCase()
      const matchClassificacao = !filtroClassificacaoNorm || classificacaoValNorm === filtroClassificacaoNorm
      
      return matchCanal && matchIdAtendimento && matchDataInicio && matchDataFim && matchNota && matchUrgencia && matchSolucionado && matchSentimento && matchAtendente && matchErrosPort && matchCompreensao && matchRiscoReabertura && matchClassificacao
    })

		// Aplicar ordenação
		return filtered.sort((a, b) => {
			const getSortValue = (item: AtendimentoData, key: SortKey): any => {
				if (key === 'attendance_date') {
					const canalLower = (item.channel ?? '').toString().toLowerCase()
					try {
						if (canalLower.includes('whatsapp')) {
							const j0 = safeParseJson(item.json)
							const o0 = j0?.output ?? j0
							const p0 = (o0?.protocolo ?? item.ticket_id ?? '') as string
							const h = p0 ? wppHeadersByProtocol[p0] : undefined
							const ds = (h?.start_datetime || h?.end_datetime) ?? null
							return ds ? new Date(ds as string).getTime() : -Infinity
						}
						if (canalLower.includes('zendesk')) {
							const h = zdHeadersByTicketId[item.ticket_id]
							const ds = h?.created_at ?? null
							return ds ? new Date(ds as string).getTime() : -Infinity
						}
					} catch {}
					return item.analysis_date ? new Date(item.analysis_date).getTime() : -Infinity
				}
				if (key === 'channel') return (item.channel ?? '').toString().toLowerCase()
				if (key === 'ticket_id') return (item.ticket_id ?? '').toString().toLowerCase()

				const j = safeParseJson(item.json)
				const o = j?.output ?? j

				switch (key) {
					case 'protocolo': {
						const p = (o?.protocolo ?? item.ticket_id ?? '') as string
						return p.toString().toLowerCase()
					}
					case 'nota':
						return typeof o?.nota === 'number' && Number.isFinite(o.nota) ? o.nota : -Infinity
					case 'urgencia':
						return o?.urgencia === true ? 1 : 0
					case 'solucionado':
						return o?.solucionado === true ? 1 : 0
					case 'sentimento':
						return String(o?.sentimentoCliente ?? '').toLowerCase()
          case 'sentimentoAtendente':
            return String(o?.sentimentoAtendente ?? '').toLowerCase()
					case 'compreensao': {
            const v = o?.compreensao
						return String(v ?? '').toLowerCase()
					}
					case 'riscoReabertura':
						return o?.riscoReabertura === true ? 1 : 0
					case 'atendente':
						return String(o?.atendente ?? '').toLowerCase()
					case 'errosPortugues': {
            const errVal = o?.errosPortuguesAtendente
						return String(errVal ?? '').toLowerCase()
					}
					case 'relacaoErros': {
            const arr = Array.isArray((o as any)?.relacaoErrosPortuguesAtendente)
              ? ((o as any).relacaoErrosPortuguesAtendente as string[])
              : []
						return Array.isArray(arr) ? arr.length : 0
					}
          case 'descricao':
            return String(o?.descricao ?? '').toLowerCase()
          case 'naoConformidade': {
            const arr = Array.isArray((o as any)?.naoConformidade)
              ? ((o as any).naoConformidade as string[])
              : []
            return Array.isArray(arr) ? arr.length : 0
          }
          case 'limitacoesAnalise': {
            const arr = Array.isArray((o as any)?.limitacoesAnalise)
              ? ((o as any).limitacoesAnalise as string[])
              : []
            return Array.isArray(arr) ? arr.length : 0
          }
					case 'oportunidades': {
						const arr = Array.isArray(o?.oportunidades) ? (o.oportunidades as string[]) : []
						return arr.length
					}
					default:
						return ''
				}
			}

			let aValue: any = getSortValue(a, sortBy)
			let bValue: any = getSortValue(b, sortBy)

			if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
			if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
			return 0
		})
	}, [atendimentosData, wppHeadersByProtocol, zdHeadersByTicketId, filtroCanal, filtroIdAtendimento, filtroDataInicio, filtroDataFim, filtroNota, filtroUrgencia, filtroSolucionado, filtroSentimento, filtroAtendente, filtroErrosPortugues, filtroCompreensao, filtroRiscoReabertura, filtroClassificacao, sortBy, sortOrder])

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [filtroCanal, filtroIdAtendimento, filtroDataInicio, filtroDataFim, filtroNota, filtroUrgencia, filtroSolucionado, filtroSentimento, filtroAtendente, filtroErrosPortugues, filtroCompreensao, filtroRiscoReabertura])

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!notaDropdownRef.current) return
      if (!notaDropdownRef.current.contains(e.target as Node)) {
        setNotaDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

	// Carregar diálogo completo quando a aba for aberta (WhatsApp)
	useEffect(() => {
		const isWhatsapp = (selectedAtendimento?.channel || '').toLowerCase().includes('whatsapp')
		if (!isDetailsModalOpen || detailsTab !== 'dialogo' || !selectedAtendimento || !isWhatsapp) return

		let cancelled = false
		const fetchDialog = async () => {
			setDialogLoading(true)
			setDialogError(null)
			setDialogHeader(null)
			setDialogItems([])
			try {
				const baseJson = safeParseJson(selectedAtendimento.json)
				const protocolo = (
					(selectedParsed as any)?.protocolo
					|| baseJson?.output?.protocolo
					|| baseJson?.protocolo
					|| selectedAtendimento.ticket_id
				) as string

				if (!protocolo) throw new Error('Protocolo não encontrado para este atendimento')

				const { data: header, error: headerError } = await supabase
					.from('wpp_ticket_list')
					.select('*')
					.eq('protocol', protocolo)
					.maybeSingle()

				if (headerError) throw headerError
				if (!cancelled) setDialogHeader((header as unknown as WppTicketHeader) || null)

				const { data: msgs, error: msgsError } = await supabase
					.from('wpp_ticket_detail')
					.select('*')
					.eq('protocol', protocolo)
					.order('datetime', { ascending: true })

				if (msgsError) throw msgsError
				if (!cancelled) setDialogItems((msgs as unknown as WppTicketMessage[]) || [])
			} catch (e: any) {
				if (!cancelled) setDialogError(e?.message || 'Erro ao carregar diálogo do WhatsApp')
			} finally {
				if (!cancelled) setDialogLoading(false)
			}
		}

		fetchDialog()
		return () => { cancelled = true }
	}, [isDetailsModalOpen, detailsTab, selectedAtendimento])

	// Carregar diálogo completo quando a aba for aberta (Zendesk)
	useEffect(() => {
		const isZendesk = (selectedAtendimento?.channel || '').toLowerCase().includes('zendesk')
		if (!isDetailsModalOpen || detailsTab !== 'dialogo' || !selectedAtendimento || !isZendesk) return

		let cancelled = false
		const fetchDialog = async () => {
			setZdLoading(true)
			setZdError(null)
			setZdHeader(null)
			setZdItems([])
			try {
				const { data: header, error: headerError } = await supabase
					.from('zendesk_ticket_list')
					.select('*')
					.eq('ticket_id', selectedAtendimento.ticket_id)
					.maybeSingle()

				if (headerError) throw headerError
				if (!cancelled) setZdHeader((header as unknown as ZendeskTicketHeader) || null)

				const { data: msgs, error: msgsError } = await supabase
					.from('zendesk_ticket_detail')
					.select('*')
					.eq('ticket_id', selectedAtendimento.ticket_id)
					.order('date', { ascending: true })

				if (msgsError) throw msgsError
				const items = (msgs as unknown as ZendeskTicketDetail[]) || []
				if (!cancelled) setZdItems(items)

				const idSet = new Set<number>()
				items.forEach((m) => {
					const idNum = m.author_id == null ? null : Number(m.author_id)
					if (idNum != null && Number.isFinite(idNum)) idSet.add(idNum)
				})
				if (idSet.size > 0) {
					const { data: users, error: usersError } = await supabase
						.from('zendesk_ticket_users')
						.select('*')
						.in('id', Array.from(idSet))
					if (usersError) throw usersError
					const map: Record<string, { name: string, role: string }> = {}
					;(users || []).forEach((u: any) => {
						map[String(u.id)] = { name: u.name, role: u.role }
					})
					if (!cancelled) setZdUsersById(map)
				} else {
					if (!cancelled) setZdUsersById({})
				}
			} catch (e: any) {
				if (!cancelled) setZdError(e?.message || 'Erro ao carregar diálogo do Zendesk')
			} finally {
				if (!cancelled) setZdLoading(false)
			}
		}

		fetchDialog()
		return () => { cancelled = true }
	}, [isDetailsModalOpen, detailsTab, selectedAtendimento])

  

  const indicadoresMetrics = useMemo(() => {
    let notasSoma = 0
    let notasCount = 0
    let solucionados = 0
    let naoSolucionados = 0
    const sentimentos = new Map<string, number>()

    atendimentosFiltrados.forEach((a) => {
		  const j = safeParseJson(a.json)
		  const o = j?.output ?? j
		  const nota = o?.nota
      if (typeof nota === 'number' && Number.isFinite(nota)) {
        notasSoma += nota
        notasCount += 1
      }
		  const sol = o?.solucionado === true
      if (sol) solucionados += 1
      else naoSolucionados += 1

		  const sent = (o?.sentimentoCliente ?? 'outros') as string
      const key = String(sent).toLowerCase()
      sentimentos.set(key, (sentimentos.get(key) || 0) + 1)
    })

    const mediaNota = notasCount > 0 ? (notasSoma / notasCount) : null
    return { mediaNota, solucionados, naoSolucionados, sentimentos }
  }, [atendimentosFiltrados])

  const riscoReaberturaData = useMemo(() => {
    let sim = 0
    let nao = 0
    atendimentosFiltrados.forEach((a) => {
      const j = safeParseJson(a.json)
      const o = j?.output ?? j
      const hasRisk = o?.riscoReabertura === true
      if (hasRisk) sim += 1
      else nao += 1
    })
    return [
      { name: 'Sim', value: sim },
      { name: 'Não', value: nao },
    ]
  }, [atendimentosFiltrados])

  const compreensaoDistribuicao = useMemo(() => {
    const counts = new Map<string, number>()
    atendimentosFiltrados.forEach((a) => {
      const j = safeParseJson(a.json)
      const o = j?.output ?? j
      const v = o?.compreensao
      const key = String(v ?? '—')
      counts.set(key, (counts.get(key) || 0) + 1)
    })
    const arr: { name: string; value: number }[] = []
    counts.forEach((value, name) => arr.push({ name, value }))
    return arr
  }, [atendimentosFiltrados])

  const errosPtDistribuicao = useMemo(() => {
    const counts = new Map<string, number>()
    atendimentosFiltrados.forEach((a) => {
      const j = safeParseJson(a.json)
      const o = j?.output ?? j
      const v = (o?.errosPortuguesAtendente as string) || ''
      const key = v ? v : '—'
      counts.set(key, (counts.get(key) || 0) + 1)
    })
    const arr: { name: string; value: number }[] = []
    counts.forEach((value, name) => arr.push({ name, value }))
    return arr
  }, [atendimentosFiltrados])

  const conformidadeDistribuicao = useMemo(() => {
    const counts = new Map<string, number>()
    atendimentosFiltrados.forEach((a) => {
      const j = safeParseJson(a.json)
      const o = j?.output ?? j
      const v = (o?.conformidade as string) || ''
      const key = v ? v : '—'
      counts.set(key, (counts.get(key) || 0) + 1)
    })
    const arr: { name: string; value: number }[] = []
    counts.forEach((value, name) => arr.push({ name, value }))
    return arr
  }, [atendimentosFiltrados])

  const atendentesDashboardData = useMemo(() => {
    const map = new Map<string, { atendente: string; total: number; solucionados: number; errosPt: number; riscoReabertura: number; somaNotas: number; countNotas: number; confCounts: Map<string, number>; sentCliCounts: Map<string, number>; sentAtendCounts: Map<string, number> }>()
    atendimentosFiltrados.forEach((a) => {
      const j = safeParseJson(a.json)
      const o = j?.output ?? j
      const nome = String(o?.atendente || '—')
      const errValRaw = (o as any)?.errosPortuguesAtendente as string | undefined
      const errNorm = normalizeText(errValRaw)
      const hasErr = (errNorm !== '' && errNorm !== '—' && errNorm !== 'nenhum')
      const isSolved = o?.solucionado === true
      const hasRisk = o?.riscoReabertura === true
      const nota = o?.nota
      if (!map.has(nome)) map.set(nome, { atendente: nome, total: 0, solucionados: 0, errosPt: 0, riscoReabertura: 0, somaNotas: 0, countNotas: 0, confCounts: new Map(), sentCliCounts: new Map(), sentAtendCounts: new Map() })
      const row = map.get(nome)!
      row.total += 1
      if (isSolved) row.solucionados += 1
      if (hasErr) row.errosPt += 1
      if (hasRisk) row.riscoReabertura += 1
      if (typeof nota === 'number' && Number.isFinite(nota)) {
        row.somaNotas += nota
        row.countNotas += 1
      }

      // Contabilizar conformidade e sentimentos
      const conf = normalizeText((o?.conformidade as string) || '')
      if (conf) row.confCounts.set(conf, (row.confCounts.get(conf) || 0) + 1)
      const scli = normalizeText((o?.sentimentoCliente as string) || '')
      if (scli) row.sentCliCounts.set(scli, (row.sentCliCounts.get(scli) || 0) + 1)
      const sat = normalizeText((o?.sentimentoAtendente as string) || '')
      if (sat) row.sentAtendCounts.set(sat, (row.sentAtendCounts.get(sat) || 0) + 1)
    })
    const orderConformidade = ['total','parcial','baixa','nenhuma']
    const orderSent = ['positivo','neutro','negativo']

    return Array.from(map.values())
      .map((r) => ({
        atendente: r.atendente,
        total: r.total,
        solucionados: r.solucionados,
        errosPt: r.errosPt,
        riscoReabertura: r.riscoReabertura,
        mediaNota: r.countNotas > 0 ? r.somaNotas / r.countNotas : null,
        conformidadeTop: pickMode(r.confCounts, orderConformidade),
        sentClienteTop: pickMode(r.sentCliCounts, orderSent),
        sentAtendTop: pickMode(r.sentAtendCounts, orderSent),
      }))
      .sort((a, b) => a.atendente.localeCompare(b.atendente))
  }, [atendimentosFiltrados])

  // Calcular dados da paginação
  const totalPages = Math.ceil(atendimentosFiltrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const atendimentosPaginados = atendimentosFiltrados.slice(startIndex, endIndex)

  const handleRowClick = (item: AtendimentoData) => {
    setSelectedAtendimento(item)
    setIsDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedAtendimento(null)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as SortKey)
      setSortOrder('asc')
    }
  }

  // Handlers de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const selectedParsed = useMemo(() => {
    if (!selectedAtendimento) return null as any
    const j = safeParseJson(selectedAtendimento.json)
    const o = j?.output ?? j

    const naoConformidade = Array.isArray(o?.naoConformidade) ? (o.naoConformidade as string[]) : []
    const oportunidades = Array.isArray(o?.oportunidades) ? (o.oportunidades as string[]) : []
    const relacaoErrosPortuguesAtendente = Array.isArray(o?.relacaoErrosPortuguesAtendente)
      ? (o.relacaoErrosPortuguesAtendente as string[])
      : []
    const limitacoesAnalise = Array.isArray(o?.limitacoesAnalise) ? (o.limitacoesAnalise as string[]) : []

    return {
      protocolo: (o?.protocolo ?? '—') as string,
      atendente: (o?.atendente ?? '—') as string,
      conformidade: (o?.conformidade ?? '—') as string,
      naoConformidade,
      solucionado: o?.solucionado === true,
      riscoReabertura: o?.riscoReabertura === true,
      descricao: (o?.descricao ?? '') as string,
      oportunidades,
      urgencia: o?.urgencia === true,
      sentimentoCliente: (o?.sentimentoCliente ?? '—') as string,
      sentimentoAtendente: (o?.sentimentoAtendente ?? '—') as string,
      compreensao: (o?.compreensao ?? '—') as string,
      nota: (typeof o?.nota === 'number' ? o.nota : '—') as number | '—',
      errosPortuguesAtendente: (o?.errosPortuguesAtendente ?? '—') as string,
      relacaoErrosPortuguesAtendente,
      limitacoesAnalise,
    }
  }, [selectedAtendimento])

  const getJsonIcon = (json: any) => {
    if (!json) return <ServerIcon className="h-5 w-5 text-gray-500" />
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />
  }

  const getJsonText = (json: any) => {
    if (!json) return 'Sem dados'
    return 'Com dados'
  }

  const getJsonColor = (json: any) => {
    if (!json) return 'text-gray-600 dark:text-gray-400'
    return 'text-green-600 dark:text-green-400'
  }

  const getCanalIcon = (canal: string) => {
    const c = (canal || '').toLowerCase()
    if (c.includes('whatsapp')) {
      return (
        <svg className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
      )
    }
    if (c.includes('zendesk')) {
      return (
        <svg className="h-5 w-5 text-emerald-600 dark:text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 7h16L4 17h16" />
        </svg>
      )
    }
    return <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-500" />
  }

  const getCanalText = (canal: string) => {
    return canal || 'Não definido'
  }

  const getCanalColor = (canal: string) => {
    return 'text-blue-600 dark:text-blue-400'
  }

  // Helpers para exibir listas como chips com truncamento e contador "+N"
  const renderChip = (text: string, key: string | number) => (
    <span
      key={key}
      title={text}
      className="px-2 py-1 text-[11px] rounded-md bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 max-w-[160px] truncate"
    >
      {text}
    </span>
  )

  const renderChips = (items: string[]) => {
    const maxVisible = 3
    const visible = items.slice(0, maxVisible)
    const remaining = items.length - visible.length
    const restTitle = remaining > 0 ? items.slice(maxVisible).join('\n') : ''
    return (
      <div className="flex flex-wrap gap-1">
        {visible.map((t, idx) => renderChip(t, idx))}
        {remaining > 0 && (
          <span
            title={restTitle}
            className="px-2 py-1 text-[11px] rounded-md bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-100"
          >
            +{remaining}
          </span>
        )}
      </div>
    )
  }

  // Versão para modal: exibir todos os itens completos
  const renderChipsFull = (items: string[]) => (
    <div className="flex flex-wrap gap-2">
      {items.map((t, idx) => (
        <span
          key={idx}
          title={t}
          className="px-2 py-1 text-[11px] rounded-md bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
        >
          {t}
        </span>
      ))}
    </div>
  )


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso as unknown as string
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
  }

  // Normaliza textos para comparações robustas (remove acentos, espaços extras e case)
  function normalizeText(value: string | null | undefined) {
    return (value ?? '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
  }

  // Retorna a classe de cor para sentimentos
  function clsForSent(v: string | null | undefined) {
    const k = normalizeText(v)
    if (k === 'positivo') return 'bg-green-100 text-green-800'
    if (k === 'negativo') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  // Seleciona o valor mais frequente (modo) com desempate por ordem semântica
  function pickMode(counts: Map<string, number>, order: string[]) {
    let best: string | null = null
    let bestCount = -1
    counts.forEach((count, key) => {
      if (count > bestCount) {
        best = key
        bestCount = count
        return
      }
      if (count === bestCount && best !== null) {
        const a = order.indexOf(normalizeText(key))
        const b = order.indexOf(normalizeText(best))
        if ((a >= 0 && b >= 0 && a < b) || (b < 0 && a >= 0) || (a < 0 && b < 0 && key < best)) {
          best = key
        }
      }
    })
    return best ?? '—'
  }

  // Converte Map em array de tuplas sem depender de iteradores do alvo ES
  function mapToEntriesArray<K, V>(m: Map<K, V>): Array<[K, V]> {
    const out: Array<[K, V]> = []
    m.forEach((v, k) => {
      out.push([k, v])
    })
    return out
  }

  const formatJson = (data: any) => {
    if (!data) return 'Sem dados'
    try {
      // Se já é uma string, tentar fazer parse primeiro
      let parsedData = data
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data)
        } catch {
          // Se não conseguir fazer parse, usar a string original
          parsedData = data
        }
      }
      
      // Formatar JSON com indentação de 2 espaços
      const formatted = JSON.stringify(parsedData, null, 2)
      
      return formatted
    } catch (error) {
      // Se falhar, retornar a string original
      return String(data)
    }
  }


  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="atendimento_analise" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      Análise de Atendimento
                    </Heading>
                    <Text size="sm" color="muted" className="mt-2">
                      ⚠️ Esta análise foi gerada por Inteligência Artificial com base nos dados de atendimentos. Para o Zendesk, somente os atendimentos finalizados, com interação do atendente e não classificados como "improdutivo" são analisados.
                    </Text>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setIsInfoModalOpen(true)} className="inline-flex items-center gap-2">
                    <InformationCircleIcon className="h-4 w-4" />
                    Informações
                  </Button>
                </div>
              </div>

              {/* Filtros (padrão SilvIA) */}
              <div className="px-6 py-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                      Filtros
                    </Heading>
                    <button
                      onClick={() => setFiltrosAbertos(prev => !prev)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      {filtrosAbertos ? (
                        <>
                          <ChevronUpIcon className="h-4 w-4" /> Recolher
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="h-4 w-4" /> Expandir
                        </>
                      )}
                    </button>
                  </div>
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Data Início
                      </Text>
                      <Input
                        type="date"
                        value={filtroDataInicio}
                        onChange={(e) => setFiltroDataInicio(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Data Fim
                      </Text>
                      <Input
                        type="date"
                        value={filtroDataFim}
                        onChange={(e) => setFiltroDataFim(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="md:col-span-3 flex flex-nowrap items-end justify-start gap-2">
                      <Button
                        variant="solid"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          if (filtroDataInicio && filtroDataFim) {
                            fetchAtendimentos()
                          }
                        }}
                        disabled={!filtroDataInicio || !filtroDataFim || loading}
                      >
                        {loading ? 'Buscando...' : 'Buscar'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          const dates = getLast30DaysDates()
                          setFiltroDataInicio(dates.inicio)
                          setFiltroDataFim(dates.fim)
                          setFiltroCanal('')
                          setFiltroIdAtendimento('')
                          setFiltroNota([])
                          setFiltroUrgencia('')
                          setFiltroSolucionado('')
                          setFiltroSentimento('')
                          setFiltroCompreensao('')
                          setFiltroAtendente('')
                          setFiltroErrosPortugues('')
                          setFiltroRiscoReabertura('')
                          setFiltroClassificacao('')
                        }}
                      >
                        Limpar Filtros
                      </Button>
                    </div>
                  </div>
                  {filtrosAbertos && (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-4">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Canal
                      </Text>
                      <Select
                        value={filtroCanal}
                        onChange={(e) => setFiltroCanal(e.target.value)}
                      >
                        <option value="">Todos os canais</option>
                        {canaisDisponiveis.map((canal) => (
                          <option key={canal} value={canal}>{canal}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-4">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        ID Atendimento
                      </Text>
                      <Input
                        type="text"
                        placeholder="Digite o ID do atendimento..."
                        value={filtroIdAtendimento}
                        onChange={(e) => setFiltroIdAtendimento(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Urgência
                      </Text>
                      <Select
                        value={filtroUrgencia}
                        onChange={(e) => setFiltroUrgencia(e.target.value)}
                      >
                        <option value="">Todas</option>
                        <option value="sim">Sim</option>
                        <option value="nao">Não</option>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Solucionado
                      </Text>
                      <Select
                        value={filtroSolucionado}
                        onChange={(e) => setFiltroSolucionado(e.target.value)}
                      >
                        <option value="">Todos</option>
                        <option value="sim">Sim</option>
                        <option value="nao">Não</option>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Sentimento
                      </Text>
                      <Select
                        value={filtroSentimento}
                        onChange={(e) => setFiltroSentimento(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {sentimentosDisponiveis.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Compreensão
                      </Text>
                      <Select
                        value={filtroCompreensao}
                        onChange={(e) => setFiltroCompreensao(e.target.value)}
                      >
                        <option value="">Todas</option>
                        {compreensoesDisponiveis.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Atendente
                      </Text>
                      <Select
                        value={filtroAtendente}
                        onChange={(e) => setFiltroAtendente(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {atendentesDisponiveis.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Erros Português
                      </Text>
                      <Select
                        value={filtroErrosPortugues}
                        onChange={(e) => setFiltroErrosPortugues(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {errosPortuguesDisponiveis.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Risco Reabertura
                      </Text>
                      <Select
                        value={filtroRiscoReabertura}
                        onChange={(e) => setFiltroRiscoReabertura(e.target.value)}
                      >
                        <option value="">Todos</option>
                        <option value="sim">Sim</option>
                        <option value="nao">Não</option>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Classificação/Tema
                      </Text>
                      <Select
                        value={filtroClassificacao}
                        onChange={(e) => setFiltroClassificacao(e.target.value)}
                      >
                        <option value="">Todas</option>
                        {classificacoesDisponiveis.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-4">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Nota
                      </Text>
                      <div className="relative" ref={notaDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setNotaDropdownOpen(v => !v)}
                          className="w-full inline-flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          <span>
                            {filtroNota.length === 0
                              ? 'Todas'
                              : filtroNota.slice().sort((a,b) => a-b).join(', ')}
                          </span>
                          <ChevronDownIcon className={`h-4 w-4 transition-transform ${notaDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {notaDropdownOpen && (
                          <div className="absolute z-20 mt-2 w-56 max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
                              const checked = filtroNota.includes(n)
                              return (
                                <label key={n} className="flex items-center gap-2 px-2 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => setFiltroNota(prev => checked ? prev.filter(x => x !== n) : [...prev, n])}
                                  />
                                  <span>{n}</span>
                                </label>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="px-6 pb-2">
                  <Text size="sm" className="text-red-600 dark:text-red-400">
                    {error}
                  </Text>
                </div>
              )}

              {/* Abas */}
              <div className="px-6">
                <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2">
                  <button
                    onClick={() => setActiveTab('indicadores')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'indicadores' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Análises
                  </button>
                  <button
                    onClick={() => setActiveTab('dashboard')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'dashboard' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => setActiveTab('indicadoresAtendente')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'indicadoresAtendente' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Indicadores por Atendente
                  </button>
                </div>
              </div>

              {/* Conteúdo por Aba */}

              {loading && (
                <div className="flex-1 flex justify-center items-center py-24 px-6">
                  <LoadingSpinner size="lg" className="mx-auto" />
                </div>
              )}

              {!loading && activeTab === 'indicadores' && (
                <div className="flex-1 px-6 py-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => scrollAnalisesBy(-320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a esquerda"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollAnalisesBy(320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a direita"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-auto">
                      <div ref={analisesScrollRef} className="overflow-x-auto" onWheel={handleAnalisesWheel}>
                        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader
                              sortKey="protocolo"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left w-48 sticky left-0 z-20 bg-gray-50 dark:bg-gray-700"
                            >
                              ID
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="attendance_date"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left w-40"
                            >
                              Data
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="nota"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Nota
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="urgencia"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              URGENTE
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="solucionado"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              SOLUCIONADO
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="sentimento"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Sentimento (Cliente)
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="sentimentoAtendente"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Sentimento (Atendente)
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="compreensao"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Compreensão
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="riscoReabertura"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              RISCO ABERTURA
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="atendente"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Atendente
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="errosPortugues"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Erros Portug.
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="descricao"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Descrição
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="naoConformidade"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Não conformidade
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="relacaoErros"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Relação de Erros de Portug.
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="limitacoesAnalise"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 text-left"
                            >
                              Limitações da Análise
                            </SortableTableHeader>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                       {atendimentosPaginados.map((item) => {
                           const j = safeParseJson(item.json)
                           const o = j?.output ?? j
                           const protocolo = o?.protocolo ?? '—'
                           const nota = typeof o?.nota === 'number' ? o.nota : '—'
                           const urgencia = o?.urgencia === true
                           const solucionado = o?.solucionado === true
                           const sentimento = (o?.sentimentoCliente ?? '—') as string
                           const sentimentoAtendente = (o?.sentimentoAtendente ?? '—') as string
                           const compreensao = (o?.compreensao ?? '—') as string
                           const riscoReabertura = o?.riscoReabertura === true
                            const atendente = (o?.atendente ?? '—') as string
                            const errosPortugues = (o?.errosPortuguesAtendente ?? '—') as string
                            const descricao = typeof o?.descricao === 'string' ? o.descricao : (o?.descricao != null ? String(o.descricao) : '—')
                            const naoConformidadeArr = Array.isArray(o?.naoConformidade) ? (o.naoConformidade as string[]) : []
                            const relacaoErrosArr = Array.isArray((o as any)?.relacaoErrosPortuguesAtendente) ? ((o as any).relacaoErrosPortuguesAtendente as string[]) : []
                            const limitacoesArr = Array.isArray(o?.limitacoesAnalise) ? (o.limitacoesAnalise as string[]) : []
                            return (
                              <tr 
								key={`${item.channel}:${item.ticket_id}`}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                onClick={() => handleRowClick(item)}
                              >
                                <td className="px-4 py-4 text-left w-48 sticky left-0 z-10 bg-gray-50 dark:bg-gray-700">
                                  <div className="flex items-center gap-2">
                                    {getCanalIcon(item.channel)}
                                    <Text size="sm" className="text-gray-900 dark:text-white font-medium truncate block">{protocolo}</Text>
                                  </div>
                                </td>
                                {(() => {
                                  const canalLower = (item.channel ?? '').toString().toLowerCase()
                                  let attendanceDateRaw: string | null = null
                                  try {
                                    if (canalLower.includes('whatsapp')) {
                                      const j0 = safeParseJson(item.json)
                                      const o0 = j0?.output ?? j0
                                      const p0 = (o0?.protocolo ?? item.ticket_id ?? '') as string
                                      const h = p0 ? wppHeadersByProtocol[p0] : undefined
                                      attendanceDateRaw = (h?.start_datetime || h?.end_datetime) ?? null
                                    } else if (canalLower.includes('zendesk')) {
                                      const h = zdHeadersByTicketId[item.ticket_id]
                                      attendanceDateRaw = h?.created_at ?? null
                                    }
                                  } catch {}
                                  return (
                                    <td className="px-4 py-4 text-left w-40">
                                      <Text size="sm" className="text-gray-500 dark:text-gray-400">{attendanceDateRaw ? formatDate(attendanceDateRaw) : '—'}</Text>
                                    </td>
                                  )
                                })()}
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-900 dark:text-white font-medium">{nota}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className={`px-2 py-1 text-xs rounded-full ${urgencia ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'} }`}>
                                    {urgencia ? 'Sim' : 'Não'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className={`px-2 py-1 text-xs rounded-full ${solucionado ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'} }`}>
                                    {solucionado ? 'Sim' : 'Não'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 capitalize">{sentimento}</span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800 capitalize">{sentimentoAtendente}</span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 capitalize">{compreensao}</span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className={`px-2 py-1 text-xs rounded-full ${riscoReabertura ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {riscoReabertura ? 'Sim' : 'Não'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-900 dark:text-white font-medium truncate block">{atendente}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 capitalize">{String(errosPortugues)}</span>
                                </td>
                                <td className="px-4 py-4 text-left max-w-xs">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">{descricao}</Text>
                                </td>
                                <td className="px-4 py-4 text-left max-w-xs">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">{naoConformidadeArr.length ? naoConformidadeArr.join(', ') : '—'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left max-w-xs">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">{relacaoErrosArr.length ? relacaoErrosArr.join(', ') : '—'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left max-w-xs">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">{limitacoesArr.length ? limitacoesArr.join(', ') : '—'}</Text>
                                </td>
                                
                              </tr>
                            )
                          })}
                    </tbody>
                      </table>
                      </div>
                    </div>
                    {totalPages > 1 && (
                      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                            <Text size="sm">
                              Mostrando {startIndex + 1} a {Math.min(endIndex, atendimentosFiltrados.length)} de {atendimentosFiltrados.length} registros
                            </Text>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handlePreviousPage}
                              disabled={currentPage === 1}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              Anterior
                            </button>
                            <div className="flex space-x-1">
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNumber: number
                                if (totalPages <= 5) {
                                  pageNumber = i + 1
                                } else if (currentPage <= 3) {
                                  pageNumber = i + 1
                                } else if (currentPage >= totalPages - 2) {
                                  pageNumber = totalPages - 4 + i
                                } else {
                                  pageNumber = currentPage - 2 + i
                                }
                                return (
                                  <button
                                    key={pageNumber}
                                    onClick={() => handlePageChange(pageNumber)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                                      currentPage === pageNumber
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    {pageNumber}
                                  </button>
                                )
                              })}
                            </div>
                            <button
                              onClick={handleNextPage}
                              disabled={currentPage === totalPages}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              Próximo
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!loading && activeTab === 'indicadoresAtendente' && (
                <div className="flex-1 px-6 py-4">
                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="p-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Atendente</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Solucionados</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Erros Portug.</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Risco Reabertura</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Média de Nota</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Conformidade mais frequente</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Sentimento (Cliente) (mais frequente)</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Sentimento (Atendente) (mais frequente)</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {atendentesDashboardData.map((row) => (
                            <tr key={row.atendente}>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">{row.atendente}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{row.total}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">{row.solucionados}</span>
                                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{row.total > 0 ? `${Math.round((row.solucionados / row.total) * 100)}%` : '—'}</span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">{row.errosPt}</span>
                                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{row.total > 0 ? `${Math.round((row.errosPt / row.total) * 100)}%` : '—'}</span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs rounded-full bg-rose-100 text-rose-800">{row.riscoReabertura}</span>
                                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{row.total > 0 ? `${Math.round((row.riscoReabertura / row.total) * 100)}%` : '—'}</span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                {row.mediaNota !== null ? row.mediaNota.toFixed(1) : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 capitalize">{row.conformidadeTop || '—'}</span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 text-xs rounded-full capitalize ${clsForSent(row.sentClienteTop)}`}>{row.sentClienteTop || '—'}</span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 text-xs rounded-full capitalize ${clsForSent(row.sentAtendTop)}`}>{row.sentAtendTop || '—'}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!loading && activeTab === 'dashboard' && (
                <div className="flex-1 px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Total Filtrados</Text>
                      <Text size="2xl" className="text-gray-900 dark:text-white font-bold">{atendimentosFiltrados.length}</Text>
                    </div>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Solucionados</Text>
                      <Text size="2xl" className="text-gray-900 dark:text-white font-bold">{indicadoresMetrics.solucionados}</Text>
                    </div>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Não Solucionados</Text>
                      <Text size="2xl" className="text-gray-900 dark:text-white font-bold">{indicadoresMetrics.naoSolucionados}</Text>
                    </div>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Taxa de Solução</Text>
                      <Text size="2xl" className="text-gray-900 dark:text-white font-bold">
                        {atendimentosFiltrados.length > 0 ? `${Math.round((indicadoresMetrics.solucionados / atendimentosFiltrados.length) * 100)}%` : '—'}
                      </Text>
                    </div>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Média de Nota</Text>
                      <Text size="2xl" className="text-gray-900 dark:text-white font-bold">
                        {indicadoresMetrics.mediaNota !== null ? indicadoresMetrics.mediaNota.toFixed(1) : '—'}
                      </Text>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                      <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Solucionados vs Não</Heading>
                      </div>
                      <div className="h-72 p-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={[{ name: 'Atendimentos', Solucionados: indicadoresMetrics.solucionados, 'Não Solucionados': indicadoresMetrics.naoSolucionados }]}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="name" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" />
                            <Tooltip formatter={barTooltipFormatter} />
                            <Legend />
                            <Bar dataKey="Solucionados" fill="#10b981" radius={[4,4,0,0]}>
                              <LabelList content={renderBarCountPercentLabel} />
                            </Bar>
                            <Bar dataKey="Não Solucionados" fill="#ef4444" radius={[4,4,0,0]}>
                              <LabelList content={renderBarCountPercentLabel} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                      <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Sentimento do Cliente</Heading>
                      </div>
                      <div className="h-72 p-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie dataKey="value" data={mapToEntriesArray(indicadoresMetrics.sentimentos).map(([k,v]) => ({ name: k, value: v }))} outerRadius={90} label={pieLabelText}>
                              {mapToEntriesArray(indicadoresMetrics.sentimentos).map(([k], idx) => (
                                <Cell key={`cell-${k}`} fill={['#10b981','#3b82f6','#f59e0b','#ef4444','#8b5cf6'][idx % 5]} />
                              ))}
                            </Pie>
                            <Legend />
                            <Tooltip content={renderPieTooltip} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                      <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Risco de Reabertura</Heading>
                      </div>
                      <div className="h-72 p-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie dataKey="value" data={riscoReaberturaData} outerRadius={90} label={pieLabelText}>
                              {riscoReaberturaData.map((d, idx) => (
                                <Cell key={d.name} fill={["#ef4444", "#10b981"][idx % 2]} />
                              ))}
                            </Pie>
                            <Legend />
                            <Tooltip content={renderPieTooltip} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Compreensão</Heading>
                    </div>
                    <div className="h-72 p-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie dataKey="value" data={compreensaoDistribuicao} outerRadius={90} label={pieLabelText}>
                            {compreensaoDistribuicao.map((d, idx) => (
                              <Cell key={d.name} fill={["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4"][idx % 6]} />
                            ))}
                          </Pie>
                          <Legend />
                          <Tooltip content={renderPieTooltip} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                      <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Erros Portug. (Atendente)</Heading>
                      </div>
                      <div className="h-72 p-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie dataKey="value" data={errosPtDistribuicao} outerRadius={90} label={pieLabelText}>
                              {errosPtDistribuicao.map((d, idx) => (
                                <Cell key={d.name} fill={["#6b7280","#f59e0b","#f97316","#ef4444","#8b5cf6","#06b6d4"][idx % 6]} />
                              ))}
                            </Pie>
                            <Legend />
                            <Tooltip content={renderPieTooltip} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                      <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Conformidade</Heading>
                      </div>
                      <div className="h-72 p-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie dataKey="value" data={conformidadeDistribuicao} outerRadius={90} label={pieLabelText}>
                              {conformidadeDistribuicao.map((d, idx) => (
                                <Cell key={d.name} fill={["#10b981","#3b82f6","#f59e0b","#ef4444","#8b5cf6","#06b6d4"][idx % 6]} />
                              ))}
                            </Pie>
                            <Legend />
                            <Tooltip content={renderPieTooltip} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </PermissionGuard>
      </div>

      {/* Modal de Detalhes */}
      {isDetailsModalOpen && selectedAtendimento && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDetailsModal}></div>
            
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6 max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Detalhes do Atendimento
                  </Heading>
					<Text size="sm" color="muted" className="mt-1">
						Ticket: {selectedAtendimento.ticket_id} - Canal: {selectedAtendimento.channel}
					</Text>
                </div>
                <button
                  onClick={handleCloseDetailsModal}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

            {/* Abas do Modal */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
              <nav className="-mb-px flex space-x-4" aria-label="Tabs">
                <button
                  onClick={() => setDetailsTab('info')}
                  className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${detailsTab === 'info' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                  Informações
                </button>
                <button
                  onClick={() => setDetailsTab('dialogo')}
                  className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${detailsTab === 'dialogo' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                  Diálogo completo
                </button>
              </nav>
            </div>

            {detailsTab === 'info' ? (
              <div className="flex-1 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Campo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          ID
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
								<Text size="sm" className="text-gray-700 dark:text-gray-300">
									{selectedAtendimento.ticket_id}
								</Text>
                      </td>
                    </tr>
                    {/* Data de Criação movida para logo após o ID */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Data de Criação
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
								<Text size="sm" className="text-gray-700 dark:text-gray-300">
									{formatDate(selectedAtendimento.analysis_date)}
								</Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Canal
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
								<div className="flex items-center gap-2">
									{getCanalIcon(selectedAtendimento.channel)}
									<Text size="sm" className={`font-medium ${getCanalColor(selectedAtendimento.channel)}`}>
										{getCanalText(selectedAtendimento.channel)}
									</Text>
								</div>
                      </td>
                    </tr>
                    
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          ID (Protocolo)
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">{selectedParsed?.protocolo}</Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Nota
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">{String(selectedParsed?.nota ?? '—')}</Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Urgência
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className={`px-2 py-1 text-xs rounded-full ${selectedParsed?.urgencia ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                          {selectedParsed?.urgencia ? 'Sim' : 'Não'}
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Solucionado
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className={`px-2 py-1 text-xs rounded-full ${selectedParsed?.solucionado ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {selectedParsed?.solucionado ? 'Sim' : 'Não'}
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Sentimento
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 capitalize">{selectedParsed?.sentimentoCliente}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Sentimento do Atendente
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 capitalize">{selectedParsed?.sentimentoAtendente}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Compreensão
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 capitalize">{selectedParsed?.compreensao}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Risco Reabertura
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className={`px-2 py-1 text-xs rounded-full ${selectedParsed?.riscoReabertura ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                          {selectedParsed?.riscoReabertura ? 'Sim' : 'Não'}
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Descrição
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                          {selectedParsed?.descricao || '—'}
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Atendente
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">{selectedParsed?.atendente}</Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Conformidade
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 capitalize">{selectedParsed?.conformidade}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Não Conformidade
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        {selectedParsed?.naoConformidade && selectedParsed.naoConformidade.length > 0 ? (
                          renderChipsFull(selectedParsed.naoConformidade)
                        ) : (
                          <Text size="xs" className="text-gray-500">—</Text>
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Erros Português (Atendente)
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 capitalize">{String(selectedParsed?.errosPortuguesAtendente ?? '—')}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Relação de Erros (Atendente)
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        {selectedParsed?.relacaoErrosPortuguesAtendente && selectedParsed.relacaoErrosPortuguesAtendente.length > 0 ? (
                          renderChipsFull(selectedParsed.relacaoErrosPortuguesAtendente)
                        ) : (
                          <Text size="xs" className="text-gray-500">—</Text>
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Oportunidades
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        {selectedParsed?.oportunidades && selectedParsed.oportunidades.length > 0 ? (
                          renderChipsFull(selectedParsed.oportunidades)
                        ) : (
                          <Text size="xs" className="text-gray-500">—</Text>
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Limitações da Análise
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        {selectedParsed?.limitacoesAnalise && selectedParsed.limitacoesAnalise.length > 0 ? (
                          renderChipsFull(selectedParsed.limitacoesAnalise)
                        ) : (
                          <Text size="xs" className="text-gray-500">—</Text>
                        )}
                      </td>
                    </tr>
                    
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                {(() => {
                  const isWhatsapp = (selectedAtendimento.channel || '').toLowerCase().includes('whatsapp')
                  const isZendesk = (selectedAtendimento.channel || '').toLowerCase().includes('zendesk')
                  if (!isWhatsapp && !isZendesk) {
                    return (
                      <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
                        Disponível apenas para atendimentos do WhatsApp e Zendesk.
                      </div>
                    )
                  }

                  // Renderização para Zendesk
                  if (isZendesk) {
                    if (zdLoading) {
                      return (
                        <div className="p-6">
                          <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                            <LoadingSpinner />
                            <Text>Carregando diálogo...</Text>
                          </div>
                        </div>
                      )
                    }
                    if (zdError) {
                      return (
                        <div className="p-6">
                          <div className="text-sm text-red-600 dark:text-red-400">{zdError}</div>
                        </div>
                      )
                    }
                    return (
                      <div className="flex flex-col gap-4 p-4">
                        {/* Header compacto do ticket Zendesk */}
                        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <Text size="sm" className="text-gray-900 dark:text-gray-100">
                            Ticket {zdHeader?.ticket_id || selectedAtendimento.ticket_id} • {zdHeader?.from || '-'} → {zdHeader?.to || '-'}
                          </Text>
                        </div>

                        {/* Mensagens */}
                        <div className="flex-1 overflow-auto">
                          {zdItems.length === 0 ? (
                            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Sem mensagens para este ticket.</div>
                          ) : (
                            <div className="px-1">
                              {zdItems.map((msg) => {
                                const authorInfo = msg.author_id != null ? zdUsersById[String(msg.author_id)] : undefined
                                const isFromAgent = (authorInfo?.role || '').toLowerCase() === 'agent'
                                const authorName = authorInfo?.name
                                const isInternal = msg.public === false
                                const internalBadgeClass = isFromAgent
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300'
                                return (
                                  <div key={msg.id} className={`flex ${isFromAgent ? 'justify-end' : 'justify-start'} mb-2`}>
                                    <div className={`${isFromAgent ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'} rounded-lg px-3 py-2 max-w-[70%] shadow-sm`}>
                                      <div className="text-[11px] opacity-75 mb-1 flex items-center gap-2">
                                        {authorName || msg.from || (isFromAgent ? 'Agente' : 'Contato')}
                                        {isInternal && (
                                          <span className={'px-1.5 py-0.5 rounded ' + internalBadgeClass}>Interna</span>
                                        )}
                                      </div>
                                      <div className="space-y-1 text-xs">
                                        <div><span className="opacity-75">ID:</span> <span className="font-medium">{msg.id}</span></div>
                                        <div><span className="opacity-75">Ticket:</span> <span className="font-medium">{msg.ticket_id}</span></div>
                                        <div><span className="opacity-75">De:</span> <span className="font-medium">{msg.from || '-'}</span></div>
                                        <div><span className="opacity-75">Para:</span> <span className="font-medium">{msg.to || '-'}</span></div>
                                        <div><span className="opacity-75">Data:</span> <span className="font-medium">{formatDateTime(msg.date)}</span></div>
                                      </div>
                                      <div className="whitespace-pre-wrap text-sm leading-relaxed break-words mt-2">{msg.description || ''}</div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  }

                  if (dialogLoading) {
                    return (
                      <div className="p-6">
                        <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                          <LoadingSpinner />
                          <Text>Carregando diálogo...</Text>
                        </div>
                      </div>
                    )
                  }

                  if (dialogError) {
                    return (
                      <div className="p-6">
                        <div className="text-sm text-red-600 dark:text-red-400">{dialogError}</div>
                      </div>
                    )
                  }

                  return (
                    <div className="flex flex-col gap-4 p-4">
                      {/* Header do ticket */}
                      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <Text size="xs" className="text-gray-500">Protocolo</Text>
                            <Text size="sm" className="text-gray-900 dark:text-gray-100">{dialogHeader?.protocol || '—'}</Text>
                          </div>
                          <div>
                            <Text size="xs" className="text-gray-500">Agente</Text>
                            <Text size="sm" className="text-gray-900 dark:text-gray-100">{dialogHeader?.agent || '—'}</Text>
                          </div>
                          <div>
                            <Text size="xs" className="text-gray-500">Telefone</Text>
                            <Text size="sm" className="text-gray-900 dark:text-gray-100">{dialogHeader?.phone || '—'}</Text>
                          </div>
                          <div>
                            <Text size="xs" className="text-gray-500">Início</Text>
                            <Text size="sm" className="text-gray-900 dark:text-gray-100">{dialogHeader?.start_datetime ? formatDate(dialogHeader.start_datetime) : '—'}</Text>
                          </div>
                          <div>
                            <Text size="xs" className="text-gray-500">Fim</Text>
                            <Text size="sm" className="text-gray-900 dark:text-gray-100">{dialogHeader?.end_datetime ? formatDate(dialogHeader.end_datetime) : '—'}</Text>
                          </div>
                          <div>
                            <Text size="xs" className="text-gray-500">Status</Text>
                            <Text size="sm" className="text-gray-900 dark:text-gray-100">{dialogHeader?.status || '—'}</Text>
                          </div>
                          <div>
                            <Text size="xs" className="text-gray-500">Nota</Text>
                            <Text size="sm" className="text-gray-900 dark:text-gray-100">{dialogHeader?.rating ?? '—'}</Text>
                          </div>
                          <div className="md:col-span-2">
                            <Text size="xs" className="text-gray-500">Classificação</Text>
                            <Text size="sm" className="text-gray-900 dark:text-gray-100">{dialogHeader?.classification || '—'}</Text>
                          </div>
                        </div>
                      </div>

                      {/* Mensagens */}
                      <div className="flex-1 overflow-auto">
                        {dialogItems.length === 0 ? (
                          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Nenhuma mensagem encontrada para este protocolo.</div>
                        ) : (
                          <div className="px-1">
                            {dialogItems.map((m) => {
                              const isAgent = m.type === 'sent'
                              return (
                                <div key={m.id} className={`flex ${isAgent ? 'justify-end' : 'justify-start'} mb-2`}>
                                  <div className={`${isAgent ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'} rounded-lg px-3 py-2 max-w-[70%] shadow-sm`}>
                                    <div className="text-[11px] opacity-75 mb-1">
                                      {m.sender || (isAgent ? 'Agente' : 'Contato')}
                                    </div>
                                    <div className="whitespace-pre-wrap text-sm leading-relaxed break-words">{m.message || ''}</div>
                                    <div className={`text-[10px] mt-1 ${isAgent ? 'text-white/80' : 'text-gray-500 dark:text-gray-300/80'}`}>{formatDate(m.datetime)}</div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Informações */}
      {isInfoModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsInfoModalOpen(false)}></div>
            
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="info-title"
              className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 max-h-[90vh] flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" id="info-title" className="text-gray-900 dark:text-white">
                  Informações - Análise de Atendimento
                </Heading>
                <button
                  onClick={() => setIsInfoModalOpen(false)}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none"
                  aria-label="Fechar informações"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Conteúdo */}
              <div className="flex-1 overflow-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <div className="flex items-center gap-2">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600" />
                    <h2 className="!mb-0">Guia Rápido – Como Funciona o Analisador de Atendimento ao Cliente</h2>
                  </div>
                  <p className="mt-2">
                    Pense no analisador como um supervisor atento: lê cada conversa, aplica um checklist e gera um relatório estruturado.
                    Verifica resolução, satisfação, erros de português (somente do atendente) e risco de reabertura.
                  </p>

                  <hr className="my-4 border-gray-200 dark:border-gray-700" />

                  <h3>1. O que ele recebe como entrada?</h3>
                  <p>Uma conversa completa, contendo:</p>
                  <ul>
                    <li>Número do protocolo</li>
                    <li>Nome do atendente</li>
                    <li>Todas as mensagens em ordem cronológica</li>
                  </ul>
                  <p>Cada mensagem inclui:</p>
                  <ul>
                    <li>Autor (cliente ou atendente)</li>
                    <li>Timestamp</li>
                    <li>Texto exato</li>
                  </ul>

                  <hr className="my-4 border-gray-200 dark:border-gray-700" />

                  <h3>2. O que ele devolve?</h3>
                  <p>Um relatório com 15 campos principais:</p>
                  <div className="overflow-x-auto">
                    <table className="table-auto w-full">
                      <thead>
                        <tr>
                          <th>Campo</th>
                          <th>O que significa</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>Protocolo</td><td>O número do atendimento</td></tr>
                        <tr><td>Atendente</td><td>Nome de quem atendeu</td></tr>
                        <tr><td>Conformidade</td><td>Se seguiu as regras da empresa</td></tr>
                        <tr><td>Problema Resolvido</td><td>Sim ou não</td></tr>
                        <tr><td>Risco de Reabertura</td><td>Chance de voltar a reclamar</td></tr>
                        <tr><td>Resumo</td><td>Breve descrição do ocorrido</td></tr>
                        <tr><td>Oportunidades de Melhoria</td><td>Ideias para melhorar</td></tr>
                        <tr><td>É Urgente?</td><td>Sim ou não</td></tr>
                        <tr><td>Humor do Cliente</td><td>Feliz, neutro ou negativo</td></tr>
                        <tr><td>Humor do Atendente</td><td>Atencioso, frio, irritado</td></tr>
                        <tr><td>Compreensão</td><td>Entendeu o problema?</td></tr>
                        <tr><td>Nota Final</td><td>De 0 a 10</td></tr>
                        <tr><td>Erros de Português (Atendente)</td><td>Quantos e quais</td></tr>
                        <tr><td>Limitações</td><td>O que não pôde ser analisado</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <hr className="my-4 border-gray-200 dark:border-gray-700" />

                  <h3>3. Como ele analisa (passo a passo)</h3>
                  <h4>Passo 1: Separa quem é quem</h4>
                  <ul>
                    <li>Cliente = quem pediu ajuda</li>
                    <li>Atendente = quem respondeu</li>
                  </ul>
                  <h4>Passo 2: Verifica conformidade</h4>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                    <p className="!mt-0">
                      Compara tudo com o manual interno. Informação errada ou procedimento não seguido → marca como não conforme.
                    </p>
                    <ul className="!mt-2">
                      <li>“Prazo é 5 dias” (manual: 3 dias)</li>
                      <li>“Não precisa de protocolo anterior” (procedimento exige)</li>
                    </ul>
                  </div>
                  <h4>Passo 3: Analisa sentimentos</h4>
                  <ul>
                    <li>Cliente: feliz, frustrado ou indiferente</li>
                    <li>Atendente: empático, robótico ou impaciente</li>
                  </ul>
                  <h4>Passo 4: Procura erros de português (apenas do atendente)</h4>
                  <ul>
                    <li>“concerteza” × “com certeza”</li>
                    <li>Gírias: “vc”, “blza”</li>
                    <li>Acentuação: “esta” × “está”</li>
                    <li>Pontuação</li>
                  </ul>
                  <p className="!mt-2">Classificação: alto (muitos), baixo (poucos), nenhum.</p>
                  <h4>Passo 5: Calcula a nota final</h4>
                  <ul>
                    <li>50% → Problema resolvido</li>
                    <li>20% → Seguiu as regras</li>
                    <li>30% → Experiência do cliente</li>
                  </ul>

                  <hr className="my-4 border-gray-200 dark:border-gray-700" />

                  <h3>4. Conceitos importantes</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                      <h4 className="!mt-0">Não Conformidade (Erro Grave)</h4>
                      <p>Regras violadas: informação incorreta, procedimento não seguido, política violada.</p>
                    </div>
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                      <h4 className="!mt-0">Oportunidade de Melhoria</h4>
                      <p>Melhorias não obrigatórias, ex.: oferecer WhatsApp, citar promoção.</p>
                    </div>
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                      <h4 className="!mt-0">Risco de Reabertura</h4>
                      <ul>
                        <li>Explicação confusa</li>
                        <li>Prazo muito longo</li>
                        <li>Dúvidas sem resposta completa</li>
                      </ul>
                    </div>
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                      <h4 className="!mt-0">Limitações de Análise</h4>
                      <p>Arquivos, imagens ou áudios não lidos são informados no relatório.</p>
                    </div>
                  </div>

                  <hr className="my-4 border-gray-200 dark:border-gray-700" />

                  <h3>5. Exemplo na prática</h3>
                  <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                    <pre className="!my-0 whitespace-pre-wrap">
Cliente: "Oi, preciso ativar meu plano"
Atendente: "Oi! Vou te ajudar. Qual é seu CPF?"
Cliente: "123.456.789-00"
Atendente: "Blza! Ativado. Qualquer dúvida me chama!"
Cliente: "Obrigado!"
                    </pre>
                  </div>
                  <p className="!mt-3">O analisador retorna:</p>
                  <ul>
                    <li>Protocolo: 95203580-81</li>
                    <li>Conformidade: Total</li>
                    <li>Resolvido: Sim</li>
                    <li>Risco de Reabertura: Não</li>
                    <li>Humor Cliente: Positivo</li>
                    <li>Humor Atendente: Positivo</li>
                    <li>Erros de Português: Baixo ("Blza" → "Beleza")</li>
                    <li>Nota: 8.5/10</li>
                    <li>Oportunidades: Oferecer outros canais</li>
                  </ul>

                  <hr className="my-4 border-gray-200 dark:border-gray-700" />

                  <h3>6. Regras que NUNCA mudam</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                    <ul className="!mt-0">
                      <li>Sempre extrai protocolo e nome do atendente corretos</li>
                      <li>Nunca critica erros do cliente, só do atendente</li>
                      <li>Sempre responde com relatório estruturado</li>
                      <li>Se for total conformidade, não lista erros</li>
                      <li>Se houve arquivo não lido, registra em limitações</li>
                      <li>A nota final reflete a realidade</li>
                    </ul>
                  </div>

                  <hr className="my-4 border-gray-200 dark:border-gray-700" />

                  <h3>7. Para que serve?</h3>
                  <ul>
                    <li>Treinamento de atendentes</li>
                    <li>Detecção de falhas de processo</li>
                    <li>Garantir satisfação do cliente</li>
                    <li>Antecipar risco de reabertura</li>
                    <li>Melhorar a qualidade geral</li>
                  </ul>

                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3">
                    <p className="!mt-0"><strong>Resumo final:</strong> O analisador mostra o que funcionou, o que falhou, o clima do cliente e como melhorar na próxima vez.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AtendimentoAnalise


